import { useTranslation } from "react-i18next";
import React, { useMemo, useState } from "react";
import styles from "./TripReviewForm.module.css";
import { Button, Text, Icon } from "@zorni/components-lib";
import { useAddTripOpinion } from "../hooks/useAddTripOpinion";
import { useNavigate } from "react-router-dom";
import { useZorni } from "../../../hooks/useZorni";
import { useOperation } from "../../../../dashboard/hooks/useOperation";

const TripReviewForm = ({ tripPublicationId, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useZorni();
  const { showToast } = useOperation();

  const [formData, setFormData] = useState({
    globalMark: 0,
    complianceMark: 0,
    qualityServiceMark: 0,
    priceQualityMark: 0,
    comment: "",
  });

  const [hoveredRatings, setHoveredRatings] = useState({
    globalMark: 0,
    complianceMark: 0,
    qualityServiceMark: 0,
    priceQualityMark: 0,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    addOpinion,
    loading: addingOpinion,
    error: mutationError,
  } = useAddTripOpinion();

  const ratingCategories = [
    {
      key: "globalMark",
      label: t("review.criteria.global", "Note globale"),
      iconName: "iconStar",
      description: t("review.criteria.globalText", "Votre impression générale du voyage."),
    },
    {
      key: "complianceMark",
      label: t("review.criteria.compliance", "Conformité"),
      iconName: "iconCompliance",
      description: t("review.criteria.complianceText", "Le voyage correspond-il à ce qui était annoncé ?"),
    },
    {
      key: "qualityServiceMark",
      label: t("review.criteria.quality", "Qualité de service"),
      iconName: "iconServiceQuality",
      description: t("review.criteria.qualityText", "Accompagnement, confort et qualité des prestations."),
    },
    {
      key: "priceQualityMark",
      label: t("review.criteria.price", "Rapport qualité / prix"),
      iconName: "iconPriceQuality",
      description: t("review.criteria.priceText", "Le tarif vous semble-t-il cohérent avec l'expérience ?"),
    },
  ];

  const averageRating = useMemo(() => {
    const values = [
      formData.globalMark,
      formData.complianceMark,
      formData.qualityServiceMark,
      formData.priceQualityMark,
    ].filter(Boolean);

    if (values.length === 0) return 0;

    return (
      values.reduce((sum, value) => sum + value, 0) / values.length
    ).toFixed(1);
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};
    if (formData.globalMark === 0) {
      newErrors.globalMark = t("review.errors.global", "La note globale est obligatoire.");
    }
    if (formData.complianceMark === 0) {
      newErrors.complianceMark = t("review.errors.compliance", "La note de conformité est obligatoire.");
    }
    if (formData.qualityServiceMark === 0) {
      newErrors.qualityServiceMark = t("review.errors.quality", "La note de qualité de service est obligatoire.");
    }
    if (formData.priceQualityMark === 0) {
      newErrors.priceQualityMark = t("review.errors.price", "La note du rapport qualité / prix est obligatoire.");
    }
    if (formData.comment.trim().length < 10) {
      newErrors.comment = t("review.errors.tooShort", "Le commentaire doit contenir au moins 10 caractères.");
    }
    if (formData.comment.length > 1000) {
      newErrors.comment = t("review.errors.tooLong", "Le commentaire ne peut pas dépasser 1000 caractères.");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRatingClick = (category, value) => {
    setFormData((prev) => ({ ...prev, [category]: value }));
    if (errors[category]) {
      setErrors((prev) => ({ ...prev, [category]: undefined }));
    }
  };

  const handleRatingHover = (category, value) => {
    setHoveredRatings((prev) => ({ ...prev, [category]: value }));
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, comment: value }));
    if (errors.comment) {
      setErrors((prev) => ({ ...prev, comment: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const tripOpinionInput = {
        clientId: userId,
        tripPublicationId,
        globalMark: formData.globalMark,
        complianceMark: formData.complianceMark,
        qualityServiceMark: formData.qualityServiceMark,
        priceQualityMark: formData.priceQualityMark,
        comment: formData.comment.trim(),
      };

      const result = await addOpinion(tripOpinionInput);

      /* L'API répond dans une enveloppe { code, error, data } : une erreur
         métier ne lève pas d'exception. Sans ce contrôle, le formulaire
         annonçait « publié » et redirigeait alors que rien n'était enregistré. */
      const response = result?.data?.MT_ADD_TRIP_OPINION;
      const code = Number(response?.code ?? 0);

      if (!response || code < 200 || code >= 300) {
        showToast(
          response?.error?.message ||
            t("review.toastError", "Une erreur est survenue. Veuillez réessayer."),
          "error"
        );
        return;
      }

      setFormData({
        globalMark: 0,
        complianceMark: 0,
        qualityServiceMark: 0,
        priceQualityMark: 0,
        comment: "",
      });

      showToast(t("review.toastSuccess", "Votre avis a été publié avec succès."), "success");

      if (typeof onSubmit === "function") {
        onSubmit(result);
        return;
      }

      navigate("/my-trips");
    } catch (error) {
      console.error("Erreur lors de la soumission :", error);
      showToast(t("review.toastError", "Une erreur est survenue. Veuillez réessayer."), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (category) => {
    const currentRating = formData[category.key];
    const hoveredRating = hoveredRatings[category.key];
    const displayRating = hoveredRating || currentRating;

    return (
      <div className={styles.starsContainer}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            id={`${category.key}Star${star}`}
            type="button"
            className={`${styles.star} ${star <= displayRating ? styles.starFilled : ""}`}
            onClick={() => handleRatingClick(category.key, star)}
            onMouseEnter={() => handleRatingHover(category.key, star)}
            onMouseLeave={() => handleRatingHover(category.key, 0)}
            aria-label={`${star} etoile${star > 1 ? "s" : ""}`}
          >
            <Icon
              name={star <= displayRating ? "iconStar" : "iconStarRegular"}
              size="lg"
              color={star <= displayRating ? "#0f172a" : "#cbd5e1"}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerBadge}>
            <Icon name="iconStar" size="sm" />
            <Text as="span" size="xs" weight="semibold">
              Avis voyageur
            </Text>
          </div>

          <div className={styles.headerTop}>
            <div className={styles.headerText}>
              <Text as="h2" size="xxl" weight="semibold">{t("review.formTitle", "Partagez votre expérience")}</Text>
              <Text as="p" size="sm" color="subtle">{t("review.formText", "Un retour clair et utile vaut mieux qu'un long texte flou. Notez les points essentiels, puis ajoutez votre ressenti personnel.")}</Text>
            </div>

            <div className={styles.averageCard}>
              <Text as="span" size="xs" weight="semibold" color="muted">{t("review.average", "Note moyenne")}</Text>
              <Text as="span" size="display" weight="bold">
                {averageRating}
              </Text>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.ratingsSection}>
            {ratingCategories.map((category) => {
              const currentRating = formData[category.key];

              return (
                <div key={category.key} className={styles.ratingCategory}>
                  <div className={styles.categoryHeader}>
                    <div className={styles.categoryLead}>
                      <span className={styles.categoryIcon}>
                        <Icon name={category.iconName} size="md" />
                      </span>
                      <div className={styles.categoryInfo}>
                        <Text as="h3" size="lg" weight="semibold">
                          {category.label}
                        </Text>
                        <Text as="p" size="sm" color="subtle">
                          {category.description}
                        </Text>
                      </div>
                    </div>

                    <div className={styles.ratingPill}>
                      <Text as="span" size="sm" weight="semibold">
                        {currentRating > 0 ? `${currentRating}/5` : t("review.notRated", "Non noté")}
                      </Text>
                    </div>
                  </div>

                  {renderStars(category)}
                  {errors[category.key] && (
                    <Text as="span" size="xs" color="danger" className={styles.error}>
                      {errors[category.key]}
                    </Text>
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.commentSection}>
            <div className={styles.commentHead}>
              <div>
                <label htmlFor="comment" className={styles.commentLabel}>{t("review.comment", "Votre commentaire")}</label>
                <Text as="p" size="sm" color="subtle">{t("review.commentHint", "Parlez du rythme, de l'organisation, des temps forts et de ce que vous recommanderiez à un autre voyageur.")}</Text>
              </div>
              <div className={styles.characterPill}>
                <Text as="span" size="xs" weight="semibold" color="muted">
                  {formData.comment.length}/1000
                </Text>
              </div>
            </div>

            <textarea
              id="comment"
              className={`${styles.commentTextarea} ${errors.comment ? styles.textareaError : ""}`}
              placeholder={t("review.placeholder", "Décrivez votre expérience de manière concrète et utile…")}
              value={formData.comment}
              onChange={handleCommentChange}
              rows={7}
              maxLength={1000}
            />

            {errors.comment && (
              <Text as="span" size="xs" color="danger" className={styles.error}>
                {errors.comment}
              </Text>
            )}
          </div>

          {mutationError && (
            <div className={styles.submitError}>Erreur GraphQL : {mutationError.message}</div>
          )}

          <div className={styles.actions}>
            {onCancel && (
              <button
                type="button"
                className={styles.cancelButton}
                onClick={onCancel}
                disabled={isSubmitting}
              >{t("action.cancel", "Annuler")}</button>
            )}
            <Button
              primary
              label={addingOpinion || isSubmitting ? t("review.sending", "Envoi…") : t("review.submit", "Publier mon avis")}
              onClick={handleSubmit}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default TripReviewForm;
